<template>
  <div v-if="visible" class="askbot-mask" @click.self="close">
    <div class="askbot-panel">
      <!-- 头部 -->
      <div class="askbot-head">
        <div class="flex items-center">
          <div class="askbot-logo">义</div>
          <div class="ml-3">
            <div class="font-semibold text-neutral-900">小义问宝</div>
            <div class="text-xs text-neutral-500">
              {{ aiEnabled ? "站内 AI 助手 · 基于站内内容回答" : "站内搜索模式" }}
            </div>
          </div>
        </div>
        <button class="askbot-icon-btn" @click="close" title="关闭">
          <i class="fa fa-close"></i>
        </button>
      </div>

      <!-- 对话区 -->
      <div class="askbot-body" ref="bodyEl">
        <div v-for="(msg, idx) in messages" :key="idx" class="mb-5">
          <!-- 用户 -->
          <div v-if="msg.role === 'user'" class="flex justify-end">
            <div class="askbot-bubble askbot-bubble-user">{{ msg.content }}</div>
          </div>
          <!-- 助手 -->
          <div v-else class="flex">
            <div class="askbot-logo askbot-logo-sm">义</div>
            <div class="ml-2 flex-1 min-w-0">
              <div class="askbot-bubble askbot-bubble-ai">
                <div v-if="msg.loading" class="text-neutral-500 text-sm">
                  <i class="fa fa-circle-o-notch fa-spin mr-1"></i>正在检索站内内容…
                </div>
                <div v-else v-html="renderText(msg.content)"></div>
              </div>
              <!-- 来源 -->
              <div v-if="msg.sources && msg.sources.length" class="mt-2">
                <div class="text-xs text-neutral-400 mb-1">相关内容</div>
                <div class="space-y-1">
                  <div
                    v-for="s in msg.sources"
                    :key="s.type + s.id"
                    class="askbot-source"
                    @click="openSource(s)"
                  >
                    <i
                      class="fa mr-1"
                      :class="sourceIcon(s.type)"
                    ></i>
                    <span class="truncate">{{ s.title }}</span>
                    <i class="fa fa-angle-right ml-auto pl-2 text-neutral-300"></i>
                  </div>
                </div>
              </div>
              <div
                v-if="msg.degraded"
                class="text-xs text-amber-500 mt-1"
              >
                <i class="fa fa-info-circle mr-1"></i>当前为站内搜索模式（AI 总结未开启）
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 快捷问题 -->
      <div v-if="messages.length <= 1" class="askbot-chips">
        <span
          v-for="q in quickQuestions"
          :key="q"
          class="askbot-chip"
          @click="ask(q)"
          >{{ q }}</span
        >
      </div>

      <!-- 输入区 -->
      <div class="askbot-foot">
        <input
          v-model="input"
          class="askbot-input"
          :maxlength="300"
          placeholder="问我站内的问题，比如：有没有批改作文的工具？"
          @keyup.enter="ask()"
        />
        <button
          class="askbot-send"
          :disabled="loading || !input.trim()"
          @click="ask()"
        >
          <i class="fa" :class="loading ? 'fa-circle-o-notch fa-spin' : 'fa-paper-plane'"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { globalState } from "../state";

export default {
  name: "AskBotModal",
  data() {
    return {
      input: "",
      loading: false,
      aiEnabled: true,
      messages: [
        {
          role: "assistant",
          content:
            "你好，我是「小义问宝」✨\n我可以帮你在本站找工具、找提示词，也能回答创值、下载、发布等站内规则问题。你想问点什么？",
        },
      ],
      quickQuestions: [
        "有没有批改作文的工具",
        "怎么上传自己的工具",
        "没有创值了怎么办",
        "PPT 课件制作技巧",
      ],
    };
  },
  computed: {
    visible() {
      return globalState.askBot.isOpen;
    },
  },
  watch: {
    visible(val) {
      if (val) {
        const q = globalState.askBot.question;
        this.$nextTick(() => {
          this.scrollBottom();
          if (q) {
            this.input = q;
            this.ask();
          }
        });
      }
    },
  },
  created() {
    // 查询是否配置了 AI Key（不影响使用，未配置时降级为站内搜索）
    this.$axios({ url: "ai/status" })
      .then((res) => {
        if (res.code === 200 && res.data) {
          this.aiEnabled = !!res.data.aiEnabled;
        }
      })
      .catch(() => {});
  },
  methods: {
    close() {
      globalState.closeAskBot();
    },
    async ask(question) {
      const q = (question || this.input || "").trim();
      if (!q || this.loading) return;
      this.input = "";
      this.messages.push({ role: "user", content: q });
      this.messages.push({ role: "assistant", content: "", loading: true });
      this.loading = true;
      this.scrollBottom();

      // 只携带最近 6 条历史
      const history = this.messages
        .filter((m) => !m.loading && m.content)
        .slice(-7, -1)
        .map((m) => ({ role: m.role, content: m.content }));

      try {
        const data = await this.$aesEncrypt({ question: q, history });
        const res = await this.$axios({
          url: "ai/ask",
          method: "POST",
          data: { data },
        });
        const cur = this.messages[this.messages.length - 1];
        if (res.code === 200 && res.data) {
          cur.content = res.data.answer || "（没有返回结果）";
          cur.sources = res.data.sources || [];
          cur.degraded = !!res.data.degraded;
        } else {
          cur.content = res.msg || "提问失败，请稍后再试";
        }
        cur.loading = false;
      } catch (e) {
        const cur = this.messages[this.messages.length - 1];
        cur.loading = false;
        cur.content = "网络异常，请稍后再试";
      }
      this.loading = false;
      this.scrollBottom();
    },
    openSource(s) {
      if (s.type === "tool") {
        this.close();
        globalState.openModal(s.id, []);
      } else {
        this.close();
        if (s.url) this.$router.push(s.url);
      }
    },
    sourceIcon(type) {
      if (type === "page") {
        return "fa-map-o text-indigo-500";
      }
      return type === "tool"
        ? "fa-cube text-primary"
        : type === "prompt"
        ? "fa-lightbulb-o text-amber-500"
        : "fa-comments-o text-green-600";
    },
    // 极简 Markdown 渲染：加粗、列表、换行
    renderText(text) {
      if (!text) return "";
      const esc = String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      let html = esc
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>");
      const lines = html.split("\n");
      let out = "";
      let inList = false;
      for (const line of lines) {
        const t = line.trim();
        const isBullet = /^([-•]|\d+[.、])\s+/.test(t);
        if (isBullet) {
          if (!inList) {
            out += "<ul class='askbot-list'>";
            inList = true;
          }
          out += "<li>" + t.replace(/^([-•]|\d+[.、])\s+/, "") + "</li>";
        } else {
          if (inList) {
            out += "</ul>";
            inList = false;
          }
          out += t ? "<p class='mb-2 last:mb-0'>" + t + "</p>" : "";
        }
      }
      if (inList) out += "</ul>";
      return out;
    },
    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.bodyEl;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.askbot-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.askbot-panel {
  width: 100%;
  max-width: 680px;
  height: 78vh;
  max-height: 720px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.askbot-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #f0f0f0;
}
.askbot-logo {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: linear-gradient(135deg, #4facfe, #0071e3);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
}
.askbot-logo-sm {
  width: 30px;
  height: 30px;
  font-size: 15px;
  border-radius: 9px;
  flex-shrink: 0;
}
.askbot-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: #86868b;
  transition: all 0.2s;
}
.askbot-icon-btn:hover {
  background: #f5f5f7;
}
.askbot-body {
  flex: 1;
  overflow-y: auto;
  padding: 18px;
  background: #fbfbfd;
}
.askbot-bubble {
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.7;
  word-break: break-word;
}
.askbot-bubble-user {
  background: #0071e3;
  color: #fff;
  max-width: 80%;
  border-bottom-right-radius: 4px;
}
.askbot-bubble-ai {
  background: #fff;
  color: #1d1d1f;
  border: 1px solid #ececed;
  max-width: 92%;
}
.askbot-chips {
  padding: 10px 18px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  border-top: 1px solid #f0f0f0;
}
.askbot-chip {
  font-size: 12px;
  color: #0071e3;
  background: rgba(0, 113, 227, 0.08);
  border-radius: 999px;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.askbot-chip:hover {
  background: rgba(0, 113, 227, 0.15);
}
.askbot-foot {
  display: flex;
  gap: 8px;
  padding: 12px 18px 16px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
}
.askbot-input {
  flex: 1;
  border: 1px solid #d2d2d7;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  outline: none;
}
.askbot-input:focus {
  border-color: #0071e3;
}
.askbot-send {
  width: 46px;
  border-radius: 10px;
  background: #0071e3;
  color: #fff;
  transition: opacity 0.2s;
}
.askbot-send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.askbot-source {
  display: flex;
  align-items: center;
  font-size: 13px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #ececed;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}
.askbot-source:hover {
  border-color: #0071e3;
  background: rgba(0, 113, 227, 0.04);
}
:deep(.askbot-list) {
  margin: 0 0 8px;
  padding-left: 20px;
  list-style: disc;
}
:deep(.askbot-list li) {
  margin-bottom: 4px;
}
</style>
